import type { ArElementSetting } from "@arc/contracts";

/**
 * The sensor settings' payload shapes and the two derivations the editors need (ADR-037 Phase 2).
 *
 * Pure, so every rule here is testable without a DOM: the legacy computed the same things inside a 250-line render
 * function that could re-enter itself asynchronously while loading a sensor.
 *
 * **`initalValues` keeps the legacy's misspelling** — Unity reads that key, so correcting it would make the field
 * dead. Same reason the server keeps it.
 */

export interface SwitchPayload {
  target: boolean;
  setupText: string;
  completedText: string;
  dontCheckInStepsAfter: boolean;
}
export interface MultiPayload {
  targets: boolean[];
  initalValues: boolean[];
  setupText: string;
  completedText: string;
  dontCheckInStepsAfter: boolean;
}
export interface RemotePayload {
  autoPlay: boolean;
  messageToSend: string;
  buttonText: string;
  setupText: string;
  completedText: string;
}
export interface DualPayload {
  targets: [boolean, boolean];
  setupText: string;
  completedText: string;
}

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const bools = (v: unknown, count: number): boolean[] => Array.from({ length: count }, (_, i) => (Array.isArray(v) ? v[i] === true : false));

/**
 * How many channels this setting has. The bound device's label list is the authority; with no device the stored
 * `targets` are the only evidence there is, which is exactly the server's rule (ADR-037 D4) mirrored here so the
 * editor renders the same number of rows the server would accept back.
 */
export function channelCount(setting: ArElementSetting): number {
  if (setting.sensor != null && setting.sensor.labels.length > 0) return setting.sensor.labels.length;
  const stored = (setting.json as { targets?: unknown } | null)?.targets;
  return Array.isArray(stored) ? stored.length : 0;
}

/**
 * One label per channel, index-preserving. `null` where the device gave no usable name — the UI prints a positional
 * fallback rather than shifting the remaining labels onto the wrong switches.
 */
export function channelLabels(setting: ArElementSetting): (string | null)[] {
  const count = channelCount(setting);
  const labels = setting.sensor?.labels ?? [];
  return Array.from({ length: count }, (_, i) => {
    const l = labels[i];
    return typeof l === "string" && l.trim() !== "" ? l : null;
  });
}

export function readSwitch(json: unknown): SwitchPayload {
  const o = (json ?? {}) as Record<string, unknown>;
  return { target: o["target"] === true, setupText: str(o["setupText"]), completedText: str(o["completedText"]), dontCheckInStepsAfter: o["dontCheckInStepsAfter"] === true };
}

export function readMulti(setting: ArElementSetting): MultiPayload {
  const o = (setting.json ?? {}) as Record<string, unknown>;
  const count = channelCount(setting);
  return {
    targets: bools(o["targets"], count),
    initalValues: bools(o["initalValues"], count),
    setupText: str(o["setupText"]),
    completedText: str(o["completedText"]),
    dontCheckInStepsAfter: o["dontCheckInStepsAfter"] === true,
  };
}

export function readRemote(json: unknown): RemotePayload {
  const o = (json ?? {}) as Record<string, unknown>;
  return {
    autoPlay: o["autoPlay"] === true,
    messageToSend: str(o["messageToSend"]),
    buttonText: str(o["buttonText"]),
    setupText: str(o["setupText"]),
    completedText: str(o["completedText"]),
  };
}

export function readDual(json: unknown): DualPayload {
  const o = (json ?? {}) as Record<string, unknown>;
  const t = bools(o["targets"], 2);
  return { targets: [t[0]!, t[1]!], setupText: str(o["setupText"]), completedText: str(o["completedText"]) };
}

/**
 * `logic` is always 0 — there is no second mode in the legacy or in any live row (ADR-037 D6). It is written only on
 * the kinds that HAVE the field: `RemoteControlSensorSetting` has never carried one, and adding it would put a key
 * into that payload the legacy never wrote.
 */
export const writeSwitch = (p: SwitchPayload): string => JSON.stringify({ ...p, logic: 0 });
export const writeMulti = (p: MultiPayload): string => JSON.stringify({ ...p, logic: 0 });
export const writeRemote = (p: RemotePayload): string => JSON.stringify(p);
export const writeDual = (p: DualPayload): string => JSON.stringify({ ...p, logic: 0 });

/** The four fixed combinations the legacy offers over a DualSwitch's pair, in its own order. */
export const DUAL_CHOICES: { key: string; pair: [boolean, boolean] }[] = [
  { key: "off-off", pair: [false, false] },
  { key: "on-off", pair: [true, false] },
  { key: "off-on", pair: [false, true] },
  { key: "on-on", pair: [true, true] },
];
export const dualKey = (pair: readonly boolean[]): string => DUAL_CHOICES.find((c) => c.pair[0] === pair[0] && c.pair[1] === pair[1])?.key ?? "off-off";

/**
 * The "Alle Schritte" set stands in for a step that precedes every step: an element bound to no section and no step
 * is shown by Unity on ALL of them, so it is where a run first touches its device (ADR-038 D10).
 *
 * Two such elements already exist in production — both bound, both authored, one carrying `initalValues` — and
 * before this they could never author their initial state, because the map walked sections → steps only.
 */
export const ALL_STEPS_KEY = -1;

/** The minimum a body read has to expose for the first-step rule — kept structural so the helper stays pure. */
interface StepLike {
  id: number;
  elements: readonly { settings: readonly { name: string; sensor: { id: number } | null }[] }[];
}
interface SectionLike {
  steps: readonly StepLike[];
}

/**
 * Is `stepId` the FIRST step, in manual order, that uses this physical sensor? (ADR-037 D7.)
 *
 * The legacy asks the same question to decide whether the `Initalwert` row is offered — a sensor's starting state is
 * a property of the whole run, so it is set once, where the run first touches the device. It answered it by scanning
 * every section, step and element inside the render, reloading sensors mid-scan and re-entering itself; here the body
 * read already has the whole manual, so it is a fold over data that is already in hand.
 */
export function isFirstStepForSensor(sections: readonly SectionLike[], stepId: number, sensorId: number, settingName: string): boolean {
  for (const section of sections) {
    for (const step of section.steps) {
      const uses = step.elements.some((el) => el.settings.some((s) => s.name === settingName && s.sensor?.id === sensorId));
      if (uses) return step.id === stepId;
    }
  }
  return false;
}

/**
 * The same question as {@link isFirstStepForSensor}, answered for the whole manual in ONE pass: step id → the sensors
 * this step is the first to use. The editor computes it once at the top and hands each card only its own set, so no
 * component below needs the whole body to decide whether to offer the `Initalwert` row.
 */
export function firstStepSensorMap(
  sections: readonly SectionLike[],
  settingName: string,
  /** The manual-level "Alle Schritte" elements, which precede every step and therefore claim a device first. */
  allStepsElements: readonly { settings: readonly { name: string; sensor: { id: number } | null }[] }[] = [],
): Map<number, Set<number>> {
  const claimed = new Set<number>();
  const out = new Map<number, Set<number>>();
  for (const el of allStepsElements) {
    for (const s of el.settings) {
      if (s.name !== settingName || s.sensor == null || claimed.has(s.sensor.id)) continue;
      claimed.add(s.sensor.id);
      const set = out.get(ALL_STEPS_KEY) ?? new Set<number>();
      set.add(s.sensor.id);
      out.set(ALL_STEPS_KEY, set);
    }
  }
  for (const section of sections) {
    for (const step of section.steps) {
      for (const el of step.elements) {
        for (const s of el.settings) {
          if (s.name !== settingName || s.sensor == null || claimed.has(s.sensor.id)) continue;
          claimed.add(s.sensor.id);
          const set = out.get(step.id) ?? new Set<number>();
          set.add(s.sensor.id);
          out.set(step.id, set);
        }
      }
    }
  }
  return out;
}
